import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  CalendarDays,
  Check,
  CloudRain,
  Droplets,
  FlaskConical,
  NotebookPen,
  Plus,
  Save,
  ScanSearch,
  Scissors,
  ShieldCheck,
  Sprout,
  Tractor,
  Trash2,
  TriangleAlert,
  Users,
  Wheat,
  Wrench,
  X,
} from "lucide-react";
import moment from "moment";
import swal from "sweetalert";
import api from "../../api/api";
import RainSourceBadge from "./RainSourceBadge";
import {
  AGRICULTURAL_EVENT_CATEGORIES,
  getAgriculturalCategory,
  parseEventDetails,
} from "../../utils/agriculturalEvents";
import {
  formatAgriculturalDate,
  formatAgriculturalTime,
} from "../../utils/agriculturalDateTime";
import "./AgriculturalEventDrawer.css";

const ICONS = {
  CalendarDays,
  CloudRain,
  Droplets,
  FlaskConical,
  NotebookPen,
  ScanSearch,
  Scissors,
  ShieldCheck,
  Sprout,
  Tractor,
  TriangleAlert,
  Users,
  Wheat,
  Wrench,
};

const emptyProduct = () => ({
  product_type: "",
  product_name: "",
  active_ingredient: "",
  registration_number: "",
  quantity: "",
  unit: "kg",
  dose_value: "",
  dose_unit: "kg/ha",
  unit_price: "",
  supplier: "",
  lot_number: "",
  notes: "",
});

const dateValue = formatAgriculturalDate;
const timeValue = formatAgriculturalTime;

const AgriculturalEventDrawer = ({
  show,
  onHide,
  fieldUid,
  initialStart,
  initialEnd,
  initialEvent,
  onSaved,
  onDeleted,
  t,
  dir = "ltr",
}) => {
  const [kind, setKind] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [subcategoryCode, setSubcategoryCode] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("17:00");
  const [executionStatus, setExecutionStatus] = useState("planned");
  const [operatorName, setOperatorName] = useState("");
  const [areaValue, setAreaValue] = useState("");
  const [areaUnit, setAreaUnit] = useState("ha");
  const [costAmount, setCostAmount] = useState("");
  const [currency, setCurrency] = useState("TND");
  const [details, setDetails] = useState({});
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const editing = Boolean(initialEvent?.id);
  const category = getAgriculturalCategory(categoryCode, initialEvent);
  const CategoryIcon = ICONS[category.icon] || CalendarDays;
  const usesProducts = ["fertilization", "crop_protection"].includes(
    categoryCode,
  );

  useEffect(() => {
    if (!show) return;
    const eventCategory = initialEvent
      ? getAgriculturalCategory(initialEvent.category_code, initialEvent).code
      : "";
    const parsedDetails = parseEventDetails(initialEvent?.details);
    const eventKind =
      eventCategory === "irrigation" || eventCategory === "rain"
        ? eventCategory
        : eventCategory
          ? "general"
          : "";

    setKind(eventKind);
    setCategoryCode(eventCategory);
    setSubcategoryCode(initialEvent?.subcategory_code || "");
    setTitle(
      initialEvent?.title &&
        !["manual Event", "Rain Event", "rain"].includes(initialEvent.title)
        ? initialEvent.title
        : "",
    );
    setDescription(initialEvent?.description || "");
    setStartDate(
      dateValue(
        initialEvent?.start_at || initialEvent?.raw_start || initialEvent?.start || initialStart,
      ) || moment().format("YYYY-MM-DD"),
    );
    setEndDate(
      dateValue(
        initialEvent?.end_at || initialEvent?.raw_end || initialEvent?.end || initialEnd || initialStart,
      ) || moment().format("YYYY-MM-DD"),
    );
    setAllDay(
      initialEvent?.all_day === undefined
        ? true
        : Boolean(initialEvent.all_day),
    );
    setStartTime(
      timeValue(initialEvent?.start_at || initialEvent?.raw_start || initialEvent?.start, "08:00"),
    );
    setEndTime(timeValue(initialEvent?.end_at || initialEvent?.raw_end || initialEvent?.end, "17:00"));
    setExecutionStatus(initialEvent?.execution_status || "planned");
    setOperatorName(initialEvent?.operator_name || "");
    setAreaValue(initialEvent?.area_value || "");
    setAreaUnit(initialEvent?.area_unit || "ha");
    setCostAmount(initialEvent?.cost_amount || "");
    setCurrency(initialEvent?.currency || "TND");
    const normalizedDetails = { ...parsedDetails };
    if (eventCategory === "rain") {
      const legacyEffectiveRain = initialEvent?.source === "rain" && parsedDetails.rainfall_basis !== "total";
      normalizedDetails.rainfall_amount = legacyEffectiveRain ? "" : normalizedDetails.rainfall_amount ?? initialEvent?.dose ?? "";
    }
    if (eventCategory === "irrigation") {
      normalizedDetails.dose = normalizedDetails.dose ?? initialEvent?.dose ?? "";
    }
    if (eventCategory === "irrigation" || eventCategory === "rain") {
      normalizedDetails.duration_unit =
        normalizedDetails.duration_unit === "hours"
          ? "hours"
          : eventCategory === "rain"
            ? "hours"
            : "minutes";
      if (
        (normalizedDetails.duration === undefined ||
          normalizedDetails.duration === null ||
          normalizedDetails.duration === "") &&
        initialEvent?.time !== undefined &&
        initialEvent?.time !== null
      ) {
        normalizedDetails.duration = String(initialEvent.time);
      }
    }
    setDetails(normalizedDetails);
    setProducts(
      (initialEvent?.products || []).map((product) => ({
        ...emptyProduct(),
        ...product,
      })),
    );
    setErrors({});
  }, [show, initialEvent, initialStart, initialEnd]);

  const productTotal = useMemo(
    () =>
      products.reduce(
        (sum, product) =>
          sum +
          (Number(product.quantity) || 0) * (Number(product.unit_price) || 0),
        0,
      ),
    [products],
  );

  const selectKind = (nextKind) => {
    setKind(nextKind);
    setCategoryCode(nextKind === "general" ? "" : nextKind);
    setSubcategoryCode("");
    setDetails({});
    setProducts([]);
  };

  const setDetail = (name, value) =>
    setDetails((current) => ({ ...current, [name]: value }));

  const durationUnit = details.duration_unit === "hours" ? "hours" : "minutes";

  const setDurationUnit = (nextUnit) => {
    setDetails((current) => {
      const currentUnit = current.duration_unit === "hours" ? "hours" : "minutes";
      const currentValue = Number(current.duration || 0);
      const minutes = currentUnit === "hours" ? currentValue * 60 : currentValue;
      const nextValue = nextUnit === "hours" ? minutes / 60 : minutes;
      return {
        ...current,
        duration: current.duration === "" ? "" : String(nextValue),
        duration_unit: nextUnit,
        duration_minutes: minutes || null,
      };
    });
  };

  const applyDuration = (days) => {
    if (!startDate) return;
    setEndDate(
      moment(startDate)
        .add(days - 1, "days")
        .format("YYYY-MM-DD"),
    );
  };

  const updateProduct = (index, name, value) => {
    setProducts((current) =>
      current.map((product, productIndex) =>
        productIndex === index ? { ...product, [name]: value } : product,
      ),
    );
  };

  const validate = () => {
    const nextErrors = {};
    if (categoryCode === "rain" && (
      details.rainfall_amount === "" || details.rainfall_amount == null
      || !Number.isFinite(Number(details.rainfall_amount)) || Number(details.rainfall_amount) < 0
    )) nextErrors.rainfall = t("invalid_rainfall_amount", "Saisissez une pluie valide en mm (zéro ou plus).");
    if (!kind) nextErrors.kind = t("agri_error_choose_type");
    if (kind === "general" && !categoryCode)
      nextErrors.category = t("agri_error_choose_category");
    if (kind === "general" && !title.trim())
      nextErrors.title = t("agri_error_title_required");
    if (!startDate || !endDate)
      nextErrors.dates = t("agri_error_dates_required");
    if (
      startDate &&
      endDate &&
      moment(endDate).isBefore(moment(startDate), "day")
    )
      nextErrors.dates = t("agri_error_end_before_start");
    if (
      !allDay &&
      startDate &&
      endDate &&
      moment(`${endDate} ${endTime}`).isBefore(
        moment(`${startDate} ${startTime}`),
      )
    )
      nextErrors.dates = t("agri_error_end_before_start");
    if (usesProducts && products.length === 0)
      nextErrors.products = t("agri_error_product_required");
    else if (
      usesProducts &&
      products.some((product) => !product.product_name.trim())
    )
      nextErrors.products = t("agri_error_product_name_required");
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => {
    const startAt = allDay
      ? moment(startDate).startOf("day")
      : moment(`${startDate} ${startTime}`, "YYYY-MM-DD HH:mm");
    const endAt = allDay
      ? moment(endDate).endOf("day")
      : moment(`${endDate} ${endTime}`, "YYYY-MM-DD HH:mm");
    const fallbackTitle =
      categoryCode === "rain"
        ? "Rain Event"
        : categoryCode === "irrigation"
          ? "manual Event"
          : t(category.labelKey);
    const durationValue = Number(details.duration || 0);
    const durationMinutes =
      categoryCode === "irrigation" || categoryCode === "rain"
        ? durationUnit === "hours"
          ? durationValue * 60
          : durationValue
        : null;
    const normalizedDetails =
      categoryCode === "irrigation" || categoryCode === "rain"
        ? {
            ...details,
            duration: details.duration ?? "",
            duration_unit: durationUnit,
            duration_minutes: Number.isFinite(durationMinutes)
              ? durationMinutes
              : null,
          }
        : details;

    return {
      ...(editing ? { event_id: initialEvent.id } : { field_uid: fieldUid }),
      title: title.trim() || fallbackTitle,
      category_code: categoryCode,
      subcategory_code: subcategoryCode || null,
      description: description.trim() || null,
      start: startAt.format("YYYY-MM-DD HH:mm:ss"),
      end: endAt.format("YYYY-MM-DD HH:mm:ss"),
      start_at: startAt.format("YYYY-MM-DD HH:mm:ss"),
      end_at: endAt.format("YYYY-MM-DD HH:mm:ss"),
      all_day: allDay,
      source: "manual",
      status: "validated",
      execution_status: executionStatus,
      operator_name: operatorName.trim() || null,
      area_value: areaValue === "" ? null : Number(areaValue),
      area_unit: areaValue === "" ? null : areaUnit,
      cost_amount:
        productTotal > 0
          ? productTotal
          : costAmount === ""
            ? null
            : Number(costAmount),
      currency,
      details: normalizedDetails,
      products: products.map((product) => ({
        ...product,
        quantity: product.quantity === "" ? null : Number(product.quantity),
        dose_value:
          product.dose_value === "" ? null : Number(product.dose_value),
        unit_price:
          product.unit_price === "" ? null : Number(product.unit_price),
        total_cost:
          (Number(product.quantity) || 0) * (Number(product.unit_price) || 0),
      })),
      dose:
        categoryCode === "irrigation"
          ? details.dose || null
          : categoryCode === "rain"
            ? Number(details.rainfall_amount)
            : null,
      time:
        categoryCode === "irrigation"
          ? Number.isFinite(durationMinutes) && durationMinutes > 0
            ? durationMinutes
            : null
          : categoryCode === "rain"
            ? Number.isFinite(durationMinutes) && durationMinutes > 0
              ? durationMinutes
              : null
            : null,
    };
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      const response = await api.post(
        editing ? "/field/edit-event" : "/field/add-event",
        payload,
      );
      if (response.data.type !== "success")
        throw new Error(response.data.message || "save_failed");
      await onSaved?.(response.data.event, categoryCode);
      onHide();
      swal({
        icon: "success",
        text: t(editing ? "agri_event_updated" : "agri_event_created"),
      });
    } catch (error) {
      console.error("Agricultural event save failed", error);
      swal({
        icon: "error",
        text: error.response?.data?.message || t("agri_event_save_error"),
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    const confirmed = await swal({
      title: t("agri_delete_title"),
      text: t("agri_delete_confirm"),
      icon: "warning",
      buttons: [t("cancel"), t("delete")],
      dangerMode: true,
    });
    if (!confirmed) return;
    setSaving(true);
    try {
      const response = await api.post("/field/delete-event", {
        event_id: initialEvent.id,
      });
      if (response.data.type !== "success")
        throw new Error(response.data.message || "delete_failed");
      await onDeleted?.(initialEvent.id, categoryCode);
      onHide();
      swal({ icon: "success", text: t("agri_event_deleted") });
    } catch (error) {
      swal({
        icon: "error",
        text: error.response?.data?.message || t("event_delete_error"),
      });
    } finally {
      setSaving(false);
    }
  };

  const renderDetailFields = () => {
    if (categoryCode === "irrigation")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_irrigation_dose")}
            type="number"
            value={details.dose}
            onChange={(value) => setDetail("dose", value)}
            suffix="mm"
          />
          <Field
            label={t("agri_duration_minutes")}
            type="number"
            value={details.duration}
            onChange={(value) => setDetail("duration", value)}
            min="0"
            step={durationUnit === "hours" ? "0.25" : "1"}
            suffix={
              durationUnit === "hours"
                ? t("agri_duration_hours_short", "h")
                : t("agri_duration_minutes_short", "min")
            }
          />
          <DurationUnitSelector
            value={durationUnit}
            onChange={setDurationUnit}
            t={t}
          />
          <SelectField
            label={t("agri_application_method")}
            value={details.method}
            onChange={(value) => setDetail("method", value)}
            options={["drip", "sprinkler", "surface", "fertigation"]}
            t={t}
          />
        </div>
      );
    if (categoryCode === "rain")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_rainfall_amount")}
            type="number"
            value={details.rainfall_amount}
            onChange={(value) => setDetail("rainfall_amount", value)}
            suffix="mm"
            min="0"
            step="any"
            required
            error={errors.rainfall}
          />
          <p className="agri-rain-help">
            {t("rain_total_help", "Saisissez la pluie totale mesurée. Le coefficient de pluie efficace sera appliqué au prochain calcul. Pour plusieurs jours, indiquez le total de la période, réparti à parts égales faute de mesures journalières.")}
          </p>
          <Field
            label={t("agri_duration")}
            type="number"
            value={details.duration}
            onChange={(value) => setDetail("duration", value)}
            min="0"
            step={durationUnit === "hours" ? "0.25" : "1"}
            suffix={
              durationUnit === "hours"
                ? t("agri_duration_hours_short", "h")
                : t("agri_duration_minutes_short", "min")
            }
          />
          <DurationUnitSelector
            value={durationUnit}
            onChange={setDurationUnit}
            t={t}
          />
        </div>
      );
    if (categoryCode === "soil_preparation")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_operation")}
            value={details.operation}
            onChange={(v) => setDetail("operation", v)}
          />
          <Field
            label={t("agri_equipment")}
            value={details.equipment}
            onChange={(v) => setDetail("equipment", v)}
          />
          <Field
            label={t("agri_depth_cm")}
            type="number"
            value={details.depth_cm}
            onChange={(v) => setDetail("depth_cm", v)}
            suffix="cm"
          />
        </div>
      );
    if (categoryCode === "sowing_planting")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_crop_variety")}
            value={details.variety}
            onChange={(v) => setDetail("variety", v)}
          />
          <Field
            label={t("agri_seed_lot")}
            value={details.seed_lot}
            onChange={(v) => setDetail("seed_lot", v)}
          />
          <Field
            label={t("agri_seed_quantity")}
            type="number"
            value={details.seed_quantity}
            onChange={(v) => setDetail("seed_quantity", v)}
          />
          <Field
            label={t("agri_planting_density")}
            value={details.density}
            onChange={(v) => setDetail("density", v)}
          />
          <Field
            label={t("agri_application_method")}
            value={details.method}
            onChange={(v) => setDetail("method", v)}
          />
        </div>
      );
    if (categoryCode === "fertilization")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_npk_composition")}
            value={details.npk}
            onChange={(v) => setDetail("npk", v)}
          />
          <Field
            label={t("agri_application_method")}
            value={details.method}
            onChange={(v) => setDetail("method", v)}
          />
        </div>
      );
    if (categoryCode === "crop_protection")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_target_pest")}
            value={details.target}
            onChange={(v) => setDetail("target", v)}
          />
          <Field
            label={t("agri_application_method")}
            value={details.method}
            onChange={(v) => setDetail("method", v)}
          />
          <Field
            label={t("agri_weather_conditions")}
            value={details.weather}
            onChange={(v) => setDetail("weather", v)}
          />
          <Field
            label={t("agri_wind_speed")}
            type="number"
            value={details.wind_speed}
            onChange={(v) => setDetail("wind_speed", v)}
            suffix="km/h"
          />
          <Field
            label={t("agri_reentry_interval")}
            type="number"
            value={details.reentry_interval}
            onChange={(v) => setDetail("reentry_interval", v)}
            suffix="h"
          />
          <Field
            label={t("agri_preharvest_interval")}
            type="number"
            value={details.preharvest_interval}
            onChange={(v) => setDetail("preharvest_interval", v)}
            suffix={t("agri_days")}
          />
        </div>
      );
    if (categoryCode === "crop_maintenance")
      return (
        <div className="agri-form-grid">
          <SelectField
            label={t("agri_operation")}
            value={details.operation}
            onChange={(v) => setDetail("operation", v)}
            options={[
              "pruning",
              "weeding",
              "mulching",
              "staking",
              "thinning",
              "pollination",
            ]}
            t={t}
          />
          <Field
            label={t("agri_equipment")}
            value={details.equipment}
            onChange={(v) => setDetail("equipment", v)}
          />
        </div>
      );
    if (categoryCode === "observation_analysis")
      return (
        <div className="agri-form-grid">
          <SelectField
            label={t("agri_observation_type")}
            value={details.observation_type}
            onChange={(v) => setDetail("observation_type", v)}
            options={[
              "scouting",
              "pest",
              "disease",
              "growth_stage",
              "soil_test",
              "water_test",
              "leaf_test",
            ]}
            t={t}
          />
          <Field
            label={t("agri_result")}
            value={details.result}
            onChange={(v) => setDetail("result", v)}
          />
        </div>
      );
    if (categoryCode === "harvest_postharvest")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_harvest_quantity")}
            type="number"
            value={details.quantity}
            onChange={(v) => setDetail("quantity", v)}
          />
          <Field
            label={t("agri_quantity_unit")}
            value={details.unit}
            onChange={(v) => setDetail("unit", v)}
          />
          <Field
            label={t("agri_quality_grade")}
            value={details.quality}
            onChange={(v) => setDetail("quality", v)}
          />
          <Field
            label={t("agri_lot_number")}
            value={details.lot_number}
            onChange={(v) => setDetail("lot_number", v)}
          />
          <Field
            label={t("agri_destination_storage")}
            value={details.destination}
            onChange={(v) => setDetail("destination", v)}
          />
          <Field
            label={t("agri_losses")}
            type="number"
            value={details.losses}
            onChange={(v) => setDetail("losses", v)}
          />
        </div>
      );
    if (categoryCode === "machinery_maintenance")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_equipment")}
            value={details.equipment}
            onChange={(v) => setDetail("equipment", v)}
          />
          <Field
            label={t("agri_machine_hours")}
            type="number"
            value={details.machine_hours}
            onChange={(v) => setDetail("machine_hours", v)}
            suffix="h"
          />
          <Field
            label={t("agri_fuel_liters")}
            type="number"
            value={details.fuel_liters}
            onChange={(v) => setDetail("fuel_liters", v)}
            suffix="L"
          />
        </div>
      );
    if (categoryCode === "labor_task")
      return (
        <div className="agri-form-grid">
          <Field
            label={t("agri_team_size")}
            type="number"
            value={details.team_size}
            onChange={(v) => setDetail("team_size", v)}
          />
          <Field
            label={t("agri_work_hours")}
            type="number"
            value={details.work_hours}
            onChange={(v) => setDetail("work_hours", v)}
            suffix="h"
          />
          <Field
            label={t("agri_hourly_cost")}
            type="number"
            value={details.hourly_cost}
            onChange={(v) => setDetail("hourly_cost", v)}
            suffix={currency}
          />
        </div>
      );
    if (categoryCode === "incident_alert")
      return (
        <div className="agri-form-grid">
          <SelectField
            label={t("agri_severity")}
            value={details.severity}
            onChange={(v) => setDetail("severity", v)}
            options={["low", "medium", "high", "critical"]}
            t={t}
          />
          <Field
            label={t("agri_corrective_action")}
            value={details.corrective_action}
            onChange={(v) => setDetail("corrective_action", v)}
          />
        </div>
      );
    return null;
  };

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      dialogClassName="agri-event-dialog"
      contentClassName="agri-event-drawer"
      backdrop="static"
      keyboard={!saving}
      dir={dir}
    >
      <Modal.Header className="agri-drawer-header">
        <div
          className="agri-header-icon"
          style={{
            color: categoryCode ? category.color : "#1d8290",
            background: categoryCode ? category.softColor : "#e8f7f9",
          }}
        >
          <CategoryIcon size={24} />
        </div>
        <div className="flex-grow-1">
          <div className="agri-eyebrow">
            {t(editing ? "agri_edit_event" : "agri_new_event")}
          </div>
          <Modal.Title>
            {editing
              ? title || t(category.labelKey)
              : t("agri_plan_field_work")}
          </Modal.Title>
        </div>
        <button
          type="button"
          className="agri-close"
          onClick={onHide}
          disabled={saving}
          aria-label={t("close")}
        >
          <X size={21} />
        </button>
      </Modal.Header>
      <Modal.Body className="agri-drawer-body">
        {categoryCode === "rain" && (
          <div className="agri-rain-notice" role="status">
            {editing && <RainSourceBadge event={initialEvent} t={t} />}
            <p>{t("rain_save_without_calculation", "La pluie est enregistrée sans recalcul automatique. Relancez le calcul pour actualiser les recommandations.")}</p>
            {initialEvent?.source === "rain" && <p>{t("rain_weather_edit_help", "Une dose corrigée devient une saisie manuelle prioritaire. Une pluie supprimée ne sera pas recréée au prochain calcul.")}</p>}
            {initialEvent?.source === "rain" && parseEventDetails(initialEvent.details).rainfall_basis !== "total" && (
              <p>{t("rain_legacy_effective_help", "Cette ancienne carte indique une pluie utile, déjà corrigée par le coefficient. Pour la remplacer, saisissez la pluie totale mesurée.")} ({Number(initialEvent.dose).toFixed(2)} mm)</p>
            )}
          </div>
        )}
        <section className="agri-section">
          <div className="agri-section-title">
            <span>1</span>
            <div>
              <strong>{t("agri_choose_event_type")}</strong>
              <small>{t("agri_choose_event_type_help")}</small>
            </div>
          </div>
          <div className="agri-kind-grid">
            {[
              {
                code: "irrigation",
                icon: Droplets,
                label: "irrigation_event",
                color: "#1976d2",
              },
              {
                code: "rain",
                icon: CloudRain,
                label: "rain_event",
                color: "#0288d1",
              },
              {
                code: "general",
                icon: Sprout,
                label: "agri_general_operation",
                color: "#26a6b7",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.code}
                  className={`agri-kind-card ${kind === item.code ? "is-selected" : ""}`}
                  style={{ "--category-color": item.color }}
                  onClick={() => selectKind(item.code)}
                  disabled={editing && getAgriculturalCategory(initialEvent?.category_code, initialEvent).code === "rain"}
                >
                  <Icon size={25} />
                  <span>{t(item.label)}</span>
                  {kind === item.code && (
                    <Check size={17} className="agri-card-check" />
                  )}
                </button>
              );
            })}
          </div>
          {errors.kind && <div className="agri-error">{errors.kind}</div>}
        </section>

        {kind === "general" && (
          <section className="agri-section agri-animate">
            <div className="agri-section-title">
              <span>2</span>
              <div>
                <strong>{t("agri_choose_category")}</strong>
                <small>{t("agri_choose_category_help")}</small>
              </div>
            </div>
            <div className="agri-category-grid">
              {AGRICULTURAL_EVENT_CATEGORIES.filter(
                (item) => !["irrigation", "rain"].includes(item.code),
              ).map((item) => {
                const Icon = ICONS[item.icon] || CalendarDays;
                return (
                  <button
                    type="button"
                    key={item.code}
                    className={`agri-category-card ${categoryCode === item.code ? "is-selected" : ""}`}
                    style={{
                      "--category-color": item.color,
                      "--category-soft": item.softColor,
                    }}
                    onClick={() => {
                      setCategoryCode(item.code);
                      setDetails({});
                      setProducts([]);
                    }}
                  >
                    <span className="agri-category-icon">
                      <Icon size={20} />
                    </span>
                    <span>{t(item.labelKey)}</span>
                    {categoryCode === item.code && <Check size={15} />}
                  </button>
                );
              })}
            </div>
            {errors.category && (
              <div className="agri-error">{errors.category}</div>
            )}
          </section>
        )}

        {categoryCode && (
          <section className="agri-section agri-animate">
            <div className="agri-section-title">
              <span>{kind === "general" ? "3" : "2"}</span>
              <div>
                <strong>{t("agri_event_information")}</strong>
                <small>{t("agri_event_information_help")}</small>
              </div>
            </div>
            <div className="agri-form-grid">
              <Field
                label={t("agri_event_title")}
                value={title}
                onChange={setTitle}
                required={kind === "general"}
                error={errors.title}
                placeholder={t("agri_event_title_placeholder")}
              />
              <Field
                label={t("agri_subcategory")}
                value={subcategoryCode}
                onChange={setSubcategoryCode}
                placeholder={t("agri_subcategory_placeholder")}
              />
            </div>
            {renderDetailFields()}
            {usesProducts && (
              <ProductEditor
                products={products}
                setProducts={setProducts}
                updateProduct={updateProduct}
                productTotal={productTotal}
                currency={currency}
                t={t}
                error={errors.products}
              />
            )}
          </section>
        )}

        {categoryCode && (
          <section className="agri-section agri-animate">
            <div className="agri-section-title">
              <span>{kind === "general" ? "4" : "3"}</span>
              <div>
                <strong>{t("agri_schedule")}</strong>
                <small>{t("agri_schedule_help")}</small>
              </div>
            </div>
            <div className="agri-quick-days">
              {[1, 2, 3].map((days) => (
                <button
                  type="button"
                  key={days}
                  onClick={() => applyDuration(days)}
                >
                  {days} {days === 1 ? t("agri_day") : t("agri_days")}
                </button>
              ))}
            </div>
            <div className="agri-form-grid">
              <Field
                label={t("start_date")}
                type="date"
                value={startDate}
                onChange={setStartDate}
              />
              <Field
                label={t("end_date")}
                type="date"
                value={endDate}
                onChange={setEndDate}
                min={startDate}
              />
            </div>
            {errors.dates && <div className="agri-error">{errors.dates}</div>}
            <Form.Check
              type="switch"
              id="agri-all-day"
              className="agri-switch"
              label={t("agri_all_day")}
              checked={allDay}
              onChange={(event) => setAllDay(event.target.checked)}
            />
            {!allDay && (
              <div className="agri-form-grid">
                <Field
                  label={t("agri_start_time")}
                  type="time"
                  value={startTime}
                  onChange={setStartTime}
                />
                <Field
                  label={t("agri_end_time")}
                  type="time"
                  value={endTime}
                  onChange={setEndTime}
                />
              </div>
            )}
          </section>
        )}

        {categoryCode && (
          <section className="agri-section agri-animate">
            <div className="agri-section-title">
              <span>{kind === "general" ? "5" : "4"}</span>
              <div>
                <strong>{t("agri_resources_and_notes")}</strong>
                <small>{t("agri_resources_and_notes_help")}</small>
              </div>
            </div>
            <div className="agri-form-grid">
              <SelectField
                label={t("agri_execution_status")}
                value={executionStatus}
                onChange={setExecutionStatus}
                options={["planned", "in_progress", "completed", "cancelled"]}
                t={t}
              />
              <Field
                label={t("agri_operator")}
                value={operatorName}
                onChange={setOperatorName}
              />
              <Field
                label={t("agri_area")}
                type="number"
                value={areaValue}
                onChange={setAreaValue}
              />
              <SelectField
                label={t("agri_area_unit")}
                value={areaUnit}
                onChange={setAreaUnit}
                options={["ha", "m2", "acre"]}
                t={t}
              />
              <Field
                label={t("agri_cost")}
                type="number"
                value={productTotal > 0 ? productTotal.toFixed(2) : costAmount}
                onChange={setCostAmount}
                disabled={productTotal > 0}
              />
              <SelectField
                label={t("agri_currency")}
                value={currency}
                onChange={setCurrency}
                options={["TND", "EUR", "USD", "MAD", "DZD"]}
                t={t}
                translateOptions={false}
              />
            </div>
            <Form.Group className="mt-3">
              <Form.Label>{t("agri_comments")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t("agri_comments_placeholder")}
              />
            </Form.Group>
          </section>
        )}
      </Modal.Body>
      <Modal.Footer className="agri-drawer-footer">
        {editing && (
          <Button variant="outline-danger" onClick={remove} disabled={saving}>
            <Trash2 size={17} /> {t("delete")}
          </Button>
        )}
        <div className="ms-auto d-flex gap-2">
          <Button variant="light" onClick={onHide} disabled={saving}>
            {t("cancel")}
          </Button>
          <Button
            className="agri-save-button"
            onClick={save}
            disabled={!categoryCode || saving}
          >
            {saving ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <Save size={17} />
            )}{" "}
            {t(editing ? "update" : "save")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const DurationUnitSelector = ({ value, onChange, t }) => (
  <Form.Group className="agri-field">
    <Form.Label>{t("agri_duration_unit", "Unité de durée")}</Form.Label>
    <div className="agri-duration-units" role="group" aria-label={t("agri_duration_unit", "Unité de durée")}>
      <button
        type="button"
        className={value === "minutes" ? "is-active" : ""}
        onClick={() => onChange("minutes")}
      >
        {t("agri_duration_minutes_short", "min")}
      </button>
      <button
        type="button"
        className={value === "hours" ? "is-active" : ""}
        onClick={() => onChange("hours")}
      >
        {t("agri_duration_hours_short", "h")}
      </button>
    </div>
  </Form.Group>
);

const Field = ({ label, value = "", onChange, suffix, error, ...props }) => (
  <Form.Group className="agri-field">
    <Form.Label>
      {label}
      {props.required && <span className="text-danger"> *</span>}
    </Form.Label>
    <div className="agri-input-wrap">
      <Form.Control
        {...props}
        aria-label={label}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        isInvalid={Boolean(error)}
      />
      {suffix && <span>{suffix}</span>}
    </div>
    {error && <div className="agri-error">{error}</div>}
  </Form.Group>
);

const SelectField = ({
  label,
  value = "",
  onChange,
  options,
  t,
  translateOptions = true,
}) => (
  <Form.Group className="agri-field">
    <Form.Label>{label}</Form.Label>
    <Form.Select
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{t("agri_choose")}</option>
      {options.map((option) => (
        <option value={option} key={option}>
          {translateOptions ? t(`agri_option_${option}`) : option}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
);

const ProductEditor = ({
  products,
  setProducts,
  updateProduct,
  productTotal,
  currency,
  t,
  error,
}) => (
  <div className="agri-products">
    <div className="agri-products-heading">
      <div>
        <strong>{t("agri_products")}</strong>
        <small>{t("agri_products_help")}</small>
      </div>
      <Button
        size="sm"
        variant="outline-success"
        onClick={() => setProducts((current) => [...current, emptyProduct()])}
      >
        <Plus size={16} /> {t("agri_add_product")}
      </Button>
    </div>
    {products.map((product, index) => (
      <div className="agri-product-card" key={index}>
        <button
          type="button"
          className="agri-product-remove"
          onClick={() =>
            setProducts((current) =>
              current.filter((_, currentIndex) => currentIndex !== index),
            )
          }
        >
          <Trash2 size={16} />
        </button>
        <div className="agri-form-grid">
          <Field
            label={t("agri_product_name")}
            value={product.product_name}
            onChange={(v) => updateProduct(index, "product_name", v)}
            required
          />
          <Field
            label={t("agri_product_type")}
            value={product.product_type}
            onChange={(v) => updateProduct(index, "product_type", v)}
          />
          <Field
            label={t("agri_active_ingredient")}
            value={product.active_ingredient}
            onChange={(v) => updateProduct(index, "active_ingredient", v)}
          />
          <Field
            label={t("agri_registration_number")}
            value={product.registration_number}
            onChange={(v) => updateProduct(index, "registration_number", v)}
          />
          <Field
            label={t("agri_quantity")}
            type="number"
            value={product.quantity}
            onChange={(v) => updateProduct(index, "quantity", v)}
          />
          <SelectField
            label={t("agri_quantity_unit")}
            value={product.unit}
            onChange={(v) => updateProduct(index, "unit", v)}
            options={["kg", "g", "l", "ml"]}
            t={t}
            translateOptions={false}
          />
          <Field
            label={t("agri_dose")}
            type="number"
            value={product.dose_value}
            onChange={(v) => updateProduct(index, "dose_value", v)}
          />
          <Field
            label={t("agri_dose_unit")}
            value={product.dose_unit}
            onChange={(v) => updateProduct(index, "dose_unit", v)}
          />
          <Field
            label={t("agri_unit_price")}
            type="number"
            value={product.unit_price}
            onChange={(v) => updateProduct(index, "unit_price", v)}
            suffix={currency}
          />
          <Field
            label={t("agri_supplier")}
            value={product.supplier}
            onChange={(v) => updateProduct(index, "supplier", v)}
          />
          <Field
            label={t("agri_lot_number")}
            value={product.lot_number}
            onChange={(v) => updateProduct(index, "lot_number", v)}
          />
        </div>
      </div>
    ))}
    {error && <div className="agri-error">{error}</div>}
    {products.length > 0 && (
      <div className="agri-products-total">
        <span>{t("agri_products_total")}</span>
        <strong>
          {productTotal.toFixed(2)} {currency}
        </strong>
      </div>
    )}
  </div>
);

export default AgriculturalEventDrawer;
